import { useNavigate, useParams } from 'react-router-dom';

import { DetailHeader } from '@/ui/DetailHeader';
import { EmptyState } from '@/ui/EmptyState';
import { Spinner } from '@/ui/Spinner';

import { CredenciadoFormulario, type ValoresDoFormulario } from './CredenciadoFormulario';
import { useCredenciado } from './dados';
import type { Credenciado } from './contrato';

/**
 * `/operacao/credenciados/:slug/editar` — tela de edição de verdade, pedida
 * pelo dono depois da migração das telas de detalhe (2026-08-27): o botão
 * "Editar" de `CredenciadoDetail.tsx` abria só o aviso de indisponibilidade, e
 * o próprio `CredenciadoFormulario` (o de "Novo Credenciado") já existia —
 * faltava reaproveitá-lo pré-preenchido, numa página própria em vez de um
 * modal.
 *
 * "SALVAR" CONTINUA SEM GRAVAR NADA — mesmo motivo de sempre (nenhuma tabela
 * cadastral aceita escrita, não há rota na API): `CredenciadoFormulario` já
 * mostra o aviso honesto, inalterado por esta página.
 *
 * `numero`, `complemento`, `cidade`, `uf` existem no formulário (fiel ao
 * protótipo) mas não no `Credenciado` desta base — o "Dados Gerais" do
 * detalhe guarda só `endereco`/`bairro`/`cep` como uma linha só. Ficam em
 * branco ao editar; não é perda de dado, é dado que a mock nunca teve.
 */
function paraValoresDoFormulario(credenciado: Credenciado): Partial<ValoresDoFormulario> {
  return {
    tipo: credenciado.tipo,
    cpf_cnpj: credenciado.cpf_cnpj,
    nome: credenciado.nome,
    nome_fantasia: credenciado.nome_fantasia ?? '',
    termo_permissionario: credenciado.termo_permissionario ?? '',
    email: credenciado.email ?? '',
    telefone: credenciado.telefone ?? '',
    telefone2: credenciado.telefone2 ?? '',
    cep: credenciado.cep ?? '',
    endereco: credenciado.endereco ?? '',
    bairro: credenciado.bairro ?? '',
    conta_banco: credenciado.conta_banco ?? '',
    conta_agencia: credenciado.conta_agencia ?? '',
    conta_numero: credenciado.conta_numero ?? '',
    conta_operacao: credenciado.conta_operacao ?? '',
    regime_tributario: credenciado.regime_tributario ?? '',
    observacoes: credenciado.observacoes ?? '',
  };
}

export function CredenciadoEditarPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: credenciado, isLoading } = useCredenciado(slug);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!credenciado) {
    return (
      <EmptyState
        title="Credenciado não encontrado"
        description="Confira o endereço ou volte para a lista de credenciados."
        actionLabel="Voltar para credenciados"
        onAction={() => navigate('/operacao/credenciados')}
      />
    );
  }

  return (
    <div className="space-y-4">
      <DetailHeader
        backTo={`/operacao/credenciados/${credenciado.slug}`}
        backLabel="Voltar"
        title="Editar Credenciado"
        subtitle={credenciado.nome}
      />
      <CredenciadoFormulario
        valoresIniciais={paraValoresDoFormulario(credenciado)}
        onCancelar={() => navigate(`/operacao/credenciados/${credenciado.slug}`)}
        rodapeInline
      />
    </div>
  );
}

export default CredenciadoEditarPage;
